import React, { JSX } from 'react';
import EDMenu from '../Menu/EDMenu';
import EDMenuItem from '../Menu/EDMenuItem';
import EDListItemIcon from '../List/Components/EDListItemIcon';
import EDIcon from '../Icon/EDIcon';
import EDDivider from '../Divider/EDDivider';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDTypography from '../Typography/EDTypography';
import { groupBy } from 'ramda';
import { PopoverOrigin } from '@mui/material/Popover';
import EDConfirmationDialog from '../ConfirmationDialog/EDConfirmationDialog';

interface IElementProps {
  onClick?: () => void;
  disabled: boolean;
  disabledReason?: string;
  confirmButtonCaption?: string;
  confirmDialogCaption?: string;
  confirmDialogMessage?: string | JSX.Element;
  category?: string;
  children?: string | JSX.Element;
  testId?: string;
}

export type LocalMenuElementType = React.ReactElement<IElementProps>;

export type ElementType =
  | Array<LocalMenuElementType | undefined | boolean>
  | LocalMenuElementType
  | undefined
  | boolean;

interface IProps {
  menuLabel?: string;
  functionMenuAnchor?: HTMLElement;
  close: (nextOnClick?: () => void) => () => void;
  selected?: number;
  noClickPropagation?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  classes?: any;
}

const styles = (theme: Theme) => ({
  menuHeaderIcon: {
    marginRight: theme.spacing(3),
  },
});

const EDLocalMenu: React.FC<React.PropsWithChildren<IProps>> = props => {
  const [isDialogopen, setDialogOpen] = React.useState(false);
  // FIXME: Ikonen functions nedan hade margin="0px 16px 0px 0px"
  const { classes } = props;
  let children: ElementType[] = [];
  if (props.children) {
    if (!Array.isArray(props.children)) {
      children = [props.children as ElementType];
    } else {
      children = props.children as ElementType[];
    }
  }
  const childIsLocalMenuElement = (
    child: ElementType
  ): child is LocalMenuElementType =>
    !(typeof child === 'undefined' || typeof child === 'boolean');

  //children som är undefined eller boolean ska ignoreras
  const childrenToRender: LocalMenuElementType[] = children.filter(
    childIsLocalMenuElement
  );

  const categorisedChildren = groupBy(child => {
    return child?.props.category || '';
  }, childrenToRender);

  let flatIndex = 0;
  const categories = Object.keys(categorisedChildren);

  const menuItemClick = (
    event: React.MouseEvent,
    onClick?: () => void,
    confirmButtonCaption?: string,
    confirmDialogCaption?: string,
    confirmDialogMessage?: string | JSX.Element
  ) => {
    if (props.noClickPropagation) {
      event.stopPropagation();
    }
    if (confirmButtonCaption && confirmDialogCaption && confirmDialogMessage) {
      setDialogOpen(true);
    } else {
      props.close(onClick)();
    }
  };

  return (
    <EDMenu
      anchorEl={props.functionMenuAnchor}
      open={props.functionMenuAnchor !== undefined}
      onClose={(event: React.MouseEvent) => menuItemClick(event)}
      anchorOrigin={props.anchorOrigin}
      transformOrigin={props.transformOrigin}
    >
      {props.menuLabel && [
        <EDMenuItem
          key="menu-label"
          onClick={() => {
            props.close();
          }}
        >
          <EDListItemIcon className={classes.menuHeaderIcon}>
            <EDIcon iconType="functions" />
          </EDListItemIcon>
          {props.menuLabel}
        </EDMenuItem>,
        <EDDivider key="divider" />,
      ]}
      {categories.map((key: string, i: number) => {
        if (i > 0) {
          flatIndex += categorisedChildren[categories[i - 1]].length;
        }
        const category = categorisedChildren[key];
        return [
          key !== '' ? (
            <EDMenuItem key={i} header>
              <EDTypography variant="body2" color="primary">
                {key}
              </EDTypography>
            </EDMenuItem>
          ) : null,
          ...category.map((x: React.ReactElement<IElementProps>, j) => {
            if (!x) {
              return null;
            }
            return (
              <EDMenuItem
                key={`${i}${j}`}
                onClick={(event: React.MouseEvent) =>
                  menuItemClick(
                    event,
                    x.props.onClick,
                    x.props.confirmButtonCaption,
                    x.props.confirmDialogCaption,
                    x.props.confirmDialogMessage
                  )
                }
                disabled={x.props.disabled}
                disabledReason={x.props.disabledReason}
                selected={flatIndex + j === props.selected}
                testId={
                  x.props.testId
                    ? x.props.testId
                    : typeof x.props.children === 'string'
                    ? x.props.children
                    : undefined
                }
              >
                <>
                  {x}
                  <EDConfirmationDialog
                    confirmCaption={x.props.confirmButtonCaption ?? ''}
                    title={x.props.confirmDialogCaption ?? ''}
                    open={isDialogopen}
                    onClose={function (): void {
                      setDialogOpen(false);
                      props.close(() => {})();
                    }}
                    onConfirm={function (): void {
                      setDialogOpen(false);
                      props.close(x.props.onClick)();
                    }}
                  >
                    {x.props.confirmDialogMessage ?? ''}
                  </EDConfirmationDialog>
                </>
              </EDMenuItem>
            );
          }),
        ];
      })}
    </EDMenu>
  );
};

export default withStyles(EDLocalMenu, styles);
